// Peaceful Web Audio Synthesizer for Romantic Ambiance
class RomanticAudioPlayer {
  private ctx: AudioContext | null = null;
  private isPlaying = false;
  private timer: number | null = null;
  private notes = [
    261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, // C4, D4, E4, G4, A4, C5, D5, E5 (Pentatonic warmth)
  ];
  private chordProgression = [
    [261.63, 329.63, 392.0, 523.25], // C major
    [220.0, 261.63, 329.63, 440.0],  // A minor
    [174.61, 220.0, 261.63, 349.23], // F major
    [196.0, 246.94, 293.66, 392.0],  // G major
  ];
  private currentChord = 0;

  private initContext() {
    if (!this.ctx && typeof window !== 'undefined') {
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          this.ctx = new AudioCtx();
        }
      } catch (e) {
        console.warn('AudioContext unavailable:', e);
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      try {
        this.ctx.resume().catch(() => {});
      } catch {}
    }
  }

  public playNote(freq: number, duration = 2.5, type: OscillatorType = 'sine', gainVal = 0.08) {
    if (!this.ctx) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      
      osc.type = type;
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime);

      gain.gain.setValueAtTime(0.001, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(gainVal, this.ctx.currentTime + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start();
      osc.stop(this.ctx.currentTime + duration);
    } catch {
      // Ignore audio errors
    }
  }

  public playChime() {
    this.initContext();
    const melody = [523.25, 659.25, 783.99, 1046.5];
    melody.forEach((freq, idx) => {
      setTimeout(() => {
        this.playNote(freq, 1.8, 'sine', 0.1);
      }, idx * 120);
    });
  }

  public playBloomSuccess() {
    this.initContext();
    const notes = [392.0, 523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, idx) => {
      setTimeout(() => {
        this.playNote(freq, 2.0, 'triangle', 0.09);
      }, idx * 90);
    });
  }

  public toggleMusic(onStateChange?: (playing: boolean) => void) {
    this.initContext();
    if (this.isPlaying) {
      this.stop();
      if (onStateChange) onStateChange(false);
    } else {
      this.start();
      if (onStateChange) onStateChange(true);
    }
  }

  public start() {
    if (this.isPlaying) return;
    this.initContext();
    this.isPlaying = true;

    const playLoop = () => {
      if (!this.isPlaying || !this.ctx) return;
      const chord = this.chordProgression[this.currentChord % this.chordProgression.length];
      this.currentChord++;

      // Play soft warm pad chord
      chord.forEach((freq, idx) => {
        setTimeout(() => {
          this.playNote(freq, 4.0, 'sine', 0.035);
        }, idx * 180);
      });

      // Random gentle bell arpeggio
      const noteCount = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < noteCount; i++) {
        const randomNote = this.notes[Math.floor(Math.random() * this.notes.length)];
        setTimeout(() => {
          if (this.isPlaying) {
            this.playNote(randomNote, 2.0, 'triangle', 0.04);
          }
        }, 800 + i * 700);
      }

      this.timer = window.setTimeout(playLoop, 3800);
    };

    playLoop();
  }

  public stop() {
    this.isPlaying = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  public getIsPlaying(): boolean {
    return this.isPlaying;
  }
}

export const romanticAudio = new RomanticAudioPlayer();
